import { HttpStatusCode } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, isDevMode } from '@angular/core';
import { environment } from '@environment';
import { FeEventsService } from '@mona/api';
import { Logger } from '@mona/shared/logger';
import { AppError } from '@mona/shared/utils';
import { MessageService } from '@mona/ui';

/**
 * Application-wide error handler that adds a UI notification to the error handling
 * provided by the default Angular ErrorHandler.
 */
export interface AppErrorHandler extends ErrorHandler {
    /**
     *
     * Handling all errors, print logs, etc
     *
     * @param error
     */
    handleApiError(error: Error & AppError): void;
}

/**
 * Application-wide error handler that adds a UI notification to the error handling
 * provided by the default Angular ErrorHandler.
 */
@Injectable()
export class AppErrorHandler extends ErrorHandler implements AppErrorHandler {
    readonly logger = new Logger('MONA');
    private messageService: MessageService;

    constructor(private injector: Injector) {
        super();
    }

    /**
     *
     * Handling all errors, print logs, etc
     *
     * @param error
     */
    handleApiError(error: AppError): void {
        if (error.status === 0) {
            error.message = `network is probably offline net::ERR_CONNECTION_REFUSED`;
        }
        // Convert RxJS TimeoutError to 408 status for consistent handling
        if (error.name === 'TimeoutError') {
            error.status = HttpStatusCode.RequestTimeout;
            error.message = `no response in timeout of ${environment.httpTimeout}ms net::ERR_CONNECTION_TIMED_OUT`;
        }

        switch (error.status) {
            case HttpStatusCode.Unauthorized:
                // handled by auth interceptor
                break;
            case HttpStatusCode.Conflict:
            case HttpStatusCode.GatewayTimeout:
                break;
            case HttpStatusCode.RequestTimeout:
                // Handle all timeout errors (RxJS timeout + server 408 responses)
                this.showOfflineToast();
                break;
            case HttpStatusCode.InternalServerError:
            case HttpStatusCode.NotFound:
            default:
                // this._dialogService.showToast({ status: 'danger', text, title, });
                this.handleError(error as any);
                break;
        }
    }

    /**
     * Show offline toast for timeout/network errors
     */
    private showOfflineToast(): void {
        // Lazy inject MessageService to avoid circular dependency
        if (!this.messageService) {
            this.messageService = this.injector.get(MessageService);
        }
        this.messageService.errorToast('errors.timeout');
    }

    /**
     * Provides a hook for centralized exception handling.
     *
     * The default implementation of `ErrorHandler` prints error messages to the `console`. To
     * intercept error handling, write a custom exception handler that replaces this default as
     * appropriate for your app.
     *
     * @param error
     */
    handleError(error: Error & AppError): void {
        const alreadyEmitted = (error as unknown as { _feEventEmitted?: boolean })?._feEventEmitted === true;
        if (!alreadyEmitted) {
            this.emitGenericError(error);
        }

        if (isDevMode()) {
            super.handleError(error);
        } else {
            const detail =
                typeof error?.stack === 'string'
                    ? error.stack.slice(0, 2048)
                    : (error.originalError as Error | undefined)?.message ?? '';
            this.logger.error(error.message, detail);
        }
    }

    /**
     * Best-effort upload of an uncaught error to the FE-events
     * pipeline. Resolved lazily so the FeEventsService doesn't have
     * to be on the DI tree for unit tests that don't import the
     * api module.
     * @param error
     */
    private emitGenericError(error: Error): void {
        try {
            const feEvents = this.injector.get(FeEventsService, null);
            if (!feEvents) {
                return;
            }
            feEvents.emit('error', {
                name: (error as Error)?.name,
                message: (error as Error)?.message,
                stack: typeof error?.stack === 'string' ? error.stack.slice(0, 2048) : undefined,
            });
        } catch {
            // observability must never crash error handling
        }
    }
}
